import {
    ExpenseReimburseMeLimit,
    FetchState,
    HsabExpenseByExternalIdRequest,
    HsabExpenseEvidenceResponse,
    HsabExpenseFormMode,
    HsabExpenseRequest,
    HsabExpenseResponse,
    HsabExpenseState,
    HsabSubmitExpenseError,
    HsabSubmitExpenseLastCompletedRequest,
    HsabSubmitExpenseRequest,
    ManagedExpenseFilterType,
    ProviderTypeAheadResponse,
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";
import * as expenseService from "@/shared/api/expenseService";
import * as storeHelper from "@/shared/storeHelper";
import * as hsabExpenseCategoryUtility from "@/employee/Utilities/HsabExpenseCategoryUtility";
import Vue from "vue";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsabExpenseEvidenceStore } from "@/employee/store/hsabExpenseEvidence";
import { useManagedExpenseStore } from "@/employee/store/managedExpense";
import { useHsabExpenseFormStore } from "@/employee/store/hsabExpenseForm";
import { useClaimStore } from "@/employee/store/claim";
import { HsabExpenseManagement } from "@/employee/route-names";

const generateEmptyState = () => ({
    wexSyncStatus: false,
    wexSyncFetchState: FetchState.Uninitialized,
    expenseTypesAndSubtypesByCategories: {},
    expenseTypesAndSubtypesByCategoriesFetchState: FetchState.Uninitialized,
    expensesByIdDictionary: {},
    expensesByIdFetchStateDictionary: {},
    expensesByExternalIdDictionary: {},
    expensesByExternalIdFetchStateDictionary: {},
    amountReimbursedToday: 0,
    dailyReimburseMeLimit: 2500,
    providerSearchTypeAheadFetchState: FetchState.Uninitialized,
    providerSearchTypeAheadResult: null,
    loadExpenseCategoriesRequest: null,
});

const initialState: HsabExpenseState = generateEmptyState();

export const useHsabExpenseStore = defineStore("hsabExpense", {
    state: (): HsabExpenseState => (_.cloneDeep(initialState)),
    getters: {
        isCategoryDataLoading: state => state.expenseTypesAndSubtypesByCategoriesFetchState === FetchState.Loading,
        isCategoryDataLoaded: state => state.expenseTypesAndSubtypesByCategoriesFetchState === FetchState.Loaded,
        isCategoryDataFailed: state => state.expenseTypesAndSubtypesByCategoriesFetchState === FetchState.Failed,
        getExpenseTypesAndSubtypesByCategory: state => category =>
            state.expenseTypesAndSubtypesByCategories[category] ?
                state.expenseTypesAndSubtypesByCategories[category].expenseTypes : [],
        getExpenseTypeByCategoryAndType: state => (category, expenseType) =>
            state.expenseTypesAndSubtypesByCategories[category] ?
                _.find(state.expenseTypesAndSubtypesByCategories[category].expenseTypes,
                    x => x.expenseType === expenseType) : null,
        isExpenseByExternalIdLoading: state => (externalId: string) =>
            state.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loading,
        getCategoriesForUsersPlans: state => {
            return hsabExpenseCategoryUtility.getAllDisplayCategories(state.expenseTypesAndSubtypesByCategories);
        },
        dailyReimburseMeLimitAmount: state => state.dailyReimburseMeLimit,
        reimbursedTodayAmount: state => state.amountReimbursedToday,
        recurringScheduleProcessingStartHour () {
            return 13;
        },
    },
    actions: {
        SetReimburseMeLimits (data: ExpenseReimburseMeLimit) {
            this.dailyReimburseMeLimit = data.dailyLimit;
            this.amountReimbursedToday = data.amountReimbursedToday;
        },
        CreateOrUpdateExpenseAndSubmitForReimbursement (expense: HsabExpenseRequest) {
            const isNewExpense = expense && expense.hsabExpenseId === null;
            const evidenceStore = useHsabExpenseEvidenceStore();
            const { stagedFilesForUpload, ...request } = expense;

            let lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.none;
            let createOrUpdateResponse: HsabExpenseResponse = null;

            const mapResponse = response => {
                lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.createOrUpdateHsabExpense;
                return response.data;
            };

            const updateCachedExpense = (saveResponse: HsabExpenseResponse) => {
                this.updateCachedExpense(saveResponse.hsabExpenseId, saveResponse);
                return saveResponse;
            };

            const uploadFiles = (saveResponse: HsabExpenseResponse) => {
                if (stagedFilesForUpload && stagedFilesForUpload.length > 0) {
                    return evidenceStore.uploadFiles(saveResponse.hsabExpenseId, stagedFilesForUpload)
                        .then(uploadResponse => {
                            if (uploadResponse.data && uploadResponse.data.errors && uploadResponse.data.errors.length > 0) {
                                const notifierStore = useNotifierStore();
                                _.forEach(uploadResponse.data.errors, errorMessage => {
                                    notifierStore.error({ error: errorMessage, rethrow: false });
                                });
                                throw new Error("Errors occured while uploading files for HsabExpenseId " +
                                    saveResponse.hsabExpenseId);
                            }
                            lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.uploadFiles;
                            return saveResponse;
                        });
                }
                lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.uploadFiles;
                return Promise.resolve(saveResponse);
            };

            const submitExpense = (response: HsabExpenseResponse) => {
                const hsabExpenseFormStore = useHsabExpenseFormStore();

                const request: HsabSubmitExpenseRequest = {
                    expenseId: response.hsabExpenseId,
                    payFromPlanOptions: hsabExpenseFormStore.currentPayFromOptions === null || hsabExpenseFormStore.currentPayFromOptions === undefined
                      ? [] : hsabExpenseFormStore.currentPayFromOptions,
                    payFromOption: hsabExpenseFormStore.payFromOption,
                    payFromIsHsaOnly: hsabExpenseFormStore.payFromHasHsaOption && !hsabExpenseFormStore.payFromHasNotionalOption,
                    excludeHsaPlans: hsabExpenseFormStore.excludeHsaPlans,
                    forceHsaPlans: hsabExpenseFormStore.forceHsaPlans,
                    isValidPreHsaStartDateExpense: hsabExpenseFormStore.isValidPreHsaStartDateExpense,
                };

                if (hsabExpenseFormStore.expenseForm.isRecurringTemplate) {
                    // recurring hsa expense with a Schedule, activate the schedule instead
                    // of submitting expense for reimbursement
                    return expenseService.ActivateHsabExpenseSchedule(request).then(_ => {
                        lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.submitForReimbursement;
                        const managedExpenseStore = useManagedExpenseStore();
                        managedExpenseStore.userHasRecurringExpenses = true;
                        return createOrUpdateResponse;
                    });
                } else if (hsabExpenseFormStore.isFutureDatedExpense) {
                    // date of service is in the future and this will be converted into a scheduled
                    // expense instead of submitting now.
                    return expenseService.convertToScheduledOneTimeExpense(request).then(response => {
                        const managedExpenseStore = useManagedExpenseStore();
                        managedExpenseStore.userHasRecurringExpenses = true;
                        return response.data;
                    });
                } else {
                    // standard submit process
                    return expenseService.submitHsabExpense(request).then(response => {
                        lastCompletedRequest = HsabSubmitExpenseLastCompletedRequest.submitForReimbursement;
                        return response.data;
                    });
                }
            };

            const reloadCurrentPageOrNavigate = async (submittedExpense: HsabExpenseResponse) => {
                if (submittedExpense) {
                    Vue.set(this.expensesByIdDictionary, submittedExpense.hsabExpenseId, submittedExpense);
                    const hsabExpenseFormStore = useHsabExpenseFormStore();
                    await hsabExpenseFormStore.loadSubmittedExpenseDetails(submittedExpense.hsabExpenseId);
                }

                const managedExpenseStore = useManagedExpenseStore();
                const currentRoute = this.router.history.current;
                const onPage = currentRoute.name === HsabExpenseManagement;
                const onRecurringFilter = onPage && currentRoute.params.filter === ManagedExpenseFilterType.RecurringExpenseTemplates;
                const hasSchedule = !!submittedExpense.schedule;
                const destinationFilter = hasSchedule ? ManagedExpenseFilterType.RecurringExpenseTemplates : ManagedExpenseFilterType.MyExpenses;

                if (hasSchedule) {
                    managedExpenseStore.forceUpdateUserHasRecurringExpense();
                }

                if (!onPage) {
                    this.router.push({
                        name: HsabExpenseManagement,
                        params: { filter: destinationFilter }
                    });
                } else {
                    if (hasSchedule && !onRecurringFilter) {
                        this.router.replace({
                            name: HsabExpenseManagement,
                            params: { filter: destinationFilter }
                        });
                    } else if (!hasSchedule && onRecurringFilter) {
                        this.router.replace({
                            name: HsabExpenseManagement,
                            params: { filter: destinationFilter }
                        });
                    } else {
                        managedExpenseStore.loadManagedExpensesByPage(managedExpenseStore.currentPage.page);
                    }
                }

                return submittedExpense;
            };

            const handleLinkedClaims = (submittedExpense: HsabExpenseResponse) => {
                if (isNewExpense && submittedExpense) {
                    this.tryLinkClaim(submittedExpense);
                } else {
                    this.refreshClaimLinks(submittedExpense);
                }
                return submittedExpense;
            };

            const reloadReimburseMeLimit = (submittedExpense: HsabExpenseResponse) => {
                this.loadExpenseReimburseMeLimit();
                return submittedExpense;
            };

            return expenseService.createOrUpdateHsabExpense(request)
                .then(result => {
                    if (result.data && result.data.hsabExpenseId) {
                        createOrUpdateResponse = result.data;
                    }
                    return result;
                })
                .then(mapResponse)
                .then(updateCachedExpense)
                .then(uploadFiles)
                .then(submitExpense)
                .then(handleLinkedClaims)
                .then(reloadCurrentPageOrNavigate)
                .then(reloadReimburseMeLimit)
                .catch(async err => {
                    const forceReloadExpense = async () => {
                        if (createOrUpdateResponse.hsabExpenseId) {
                            const expenseFormStore = useHsabExpenseFormStore();
                            await expenseFormStore.loadExpenseForViewOrEdit(createOrUpdateResponse.hsabExpenseId);
                            const managedExpenseStore = useManagedExpenseStore();
                            await managedExpenseStore.loadManagedExpensesByPage(1, this.filter);
                            expenseFormStore.expenseForm.hsabExpenseEvidenceShoeboxIds = [];
                        }
                    };
                    let msg = err;
                    let step = HsabSubmitExpenseError.None;
                    switch (lastCompletedRequest) {
                        case HsabSubmitExpenseLastCompletedRequest.none:
                            msg = "Failed to save the expense. Please try again.";
                            step = HsabSubmitExpenseError.CreateExpense;
                            break;
                        case HsabSubmitExpenseLastCompletedRequest.createOrUpdateHsabExpense:
                            msg = "The expense was saved but failed to upload evidence file(s). The expense has not been submitted. Please check the attached receipts.";
                            await forceReloadExpense();
                            step = HsabSubmitExpenseError.UploadFiles;
                            break;
                        case HsabSubmitExpenseLastCompletedRequest.uploadFiles:
                            msg = "The expense was saved but failed to submit for reimbursement.";
                            if (err && err.response && err.response.data) {
                                msg += ` ${err.response.data.errorMessage}`;
                            }
                            await forceReloadExpense();
                            step = HsabSubmitExpenseError.SubmitForReimbursement;
                            break;
                    }
                    storeHelper.handleError({ error: msg, rethrow: false });
                    throw step;
                });
        },
        async loadExpenseReimburseMeLimit () {
            return expenseService.getExpenseReimburseMeLimit ()
            .then(response => this.SetReimburseMeLimits(response.data));
        },
        tryLinkClaim (expenseResponse: HsabExpenseResponse) {
            if (expenseResponse
                && expenseResponse.linkedClaimNumbers !== null
                && expenseResponse.linkedClaimNumbers.length > 0) {
                  const request = {
                    claimNumber: expenseResponse.linkedClaimNumbers[0],
                    expenseIds: [ expenseResponse.hsabExpenseId ],
                    comment: expenseResponse.comment
                  };
                  const claimStore = useClaimStore();
                  claimStore.linkHsabExpensesToClaim(request);
            }
            return expenseResponse;
        },
        refreshClaimLinks (expenseResponse: HsabExpenseResponse) {
            if (expenseResponse
                && expenseResponse.linkedClaimNumbers !== null
                && expenseResponse.linkedClaimNumbers.length > 0) {
                  const claimStore = useClaimStore();
                  claimStore.refreshLinkedHsabExpenses(expenseResponse.linkedClaimNumbers[0]);
            }
            return expenseResponse;
        },
        setProviderSearchTypeAheadFetchState (fetchState: FetchState) {
          this.providerSearchTypeAheadFetchState = fetchState;
        },
        setProviderSearchTypeAheadResult (data: ProviderTypeAheadResponse) {
          this.providerSearchTypeAheadResult = data;
        },
        getProviderSearchTypeAhead (searchTerm: string) {
          this.setProviderSearchTypeAheadFetchState(FetchState.Loading);
          return expenseService.getProviderSearchTypeAhead(searchTerm)
          .then(response => {
            this.setProviderSearchTypeAheadResult(response.data);
          }).catch(err => {
            storeHelper.handleError({ error: err, rethrow: true, timeout: 3000 });
          }).finally(() => this.setProviderSearchTypeAheadFetchState(FetchState.Loaded));
        },
        getProviderLookupPreCache () {
          return expenseService.getProviderLookupPreCache()
            .catch(() => {
                // don't care
            });
        },
        saveExpense (expense: HsabExpenseRequest): Promise<any> {
            const { stagedFilesForUpload, ...request } = expense;
            const isNewExpense = expense && expense.hsabExpenseId === null;
            const handleLinkedClaims = (submittedExpense: HsabExpenseResponse) => {
                if (isNewExpense) {
                    this.tryLinkClaim(submittedExpense);
                } else {
                    this.refreshClaimLinks(submittedExpense);
                }
                return submittedExpense;
            };
            return expenseService.createOrUpdateHsabExpense(expense)
                .then(response => {
                    this.clearCachedExpenseById(response.data.hsabExpenseId);
                    return response.data;
                })
                .then(handleLinkedClaims)
                .then(saveResponse => {
                    const evidenceStore = useHsabExpenseEvidenceStore();
                    if (stagedFilesForUpload && stagedFilesForUpload.length > 0) {
                        return evidenceStore.uploadFiles(saveResponse.hsabExpenseId, stagedFilesForUpload)
                            .then(uploadResponse => {
                                // todo display errors if any
                                return saveResponse;
                            });
                    }
                    return Promise.resolve(saveResponse);
                })
                .then(_ => {
                    const managedExpenseStore = useManagedExpenseStore();
                    managedExpenseStore.loadManagedExpensesByPage(managedExpenseStore.currentPage.page);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        syncWexExpenses (skipSync = false) {
            if (this.wexSyncStatus || skipSync) {
                return Promise.resolve();
            }
            const hsabAccountsStore = useHsabAccountsStore();
            if (hsabAccountsStore.notionalAccounts.length === 0) {
                this.wexSyncFetchState = FetchState.Loaded;
                this.wexSyncStatus = true;
                return Promise.resolve();
            }
            this.wexSyncFetchState = FetchState.Loading;

            return expenseService.syncHsabExpenses().then(response => {
                if (response.data) {
                    this.wexSyncStatus = true;
                    this.wexSyncFetchState = FetchState.Loaded;
                } else {
                    this.wexSyncFetchState = FetchState.Failed;
                }
            }).catch(err => {
                this.wexSyncFetchState = FetchState.Failed;

                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        async deleteExpense (expenseId: number) {
            const notifierStore = useNotifierStore();
            return expenseService.deleteHsabExpense(expenseId)
                .then(() => {
                    const claimStore = useClaimStore();
                    claimStore.removeLinksToExpense(expenseId);
                })
                .then(() => {
                    notifierStore.setSuccess("Expense deleted successfully.", notifierStore.mediumTimeout);
                    const managedExpenseStore = useManagedExpenseStore();
                    managedExpenseStore.loadManagedExpensesByPage(managedExpenseStore.currentPage.page);
                })
                .catch(() => {
                    notifierStore.setDanger("Unable to delete expense.", notifierStore.mediumTimeout);
                });
        },
        loadExpenseCategories () {
            if (this.expenseTypesAndSubtypesByCategoriesFetchState === FetchState.Loaded) {
                return Promise.resolve();
            }
            if (this.expenseTypesAndSubtypesByCategoriesFetchState === FetchState.Loading) {
                return this.loadExpenseCategoriesRequest; // request already in progress
            }

            this.expenseTypesAndSubtypesByCategoriesFetchState = FetchState.Loading;

            this.loadExpenseCategoriesRequest = expenseService.getHsabExpenseCategories()
                .then(result => {
                    this.expenseTypesAndSubtypesByCategories = result.data;
                    this.expenseTypesAndSubtypesByCategoriesFetchState = FetchState.Loaded;
                    this.loadExpenseCategoriesRequest = null;
                    return Promise.resolve();
                })
                .catch(() => {
                    this.expenseTypesAndSubtypesByCategoriesFetchState = FetchState.Failed;
                    this.loadExpenseCategoriesRequest = null;
                    return Promise.resolve();
                });
            return this.loadExpenseCategoriesRequest;
        },
        updateCachedExpenseEvidence (expenseId: number, evidence: HsabExpenseEvidenceResponse[]) {
            const expense = this.expensesByIdDictionary[expenseId];
            if (!expense) return;
            Vue.set(expense, "expenseEvidence", evidence);
        },
        updateCachedExpense (expenseId: number, expense: HsabExpenseResponse) {
            if (expense) {
                this.expensesByIdFetchStateDictionary[expenseId] = FetchState.Loaded;
            } else {
                this.expensesByIdFetchStateDictionary[expenseId] = FetchState.Uninitialized;
            }
            Vue.set(this.expensesByIdDictionary, expenseId, expense);
        },
        async getSubmitLogsById (hsabExpenseId: number, args?: { username: string, partnerId: number }) {
            return expenseService.getSubmitLogsById(hsabExpenseId, args).then(response => {
                return response.data;
            }).catch(err => storeHelper.handleError({ error: err, rethrow: false, timeout: 3000 }));
        },
        async GetSubmitLogByFileName (hsabExpenseId: number, filename: string, args?: { username: string, partnerId: number }) {
            return expenseService.GetSubmitLogByFileName(hsabExpenseId, filename, args).then(response => {
                return response.data;
            }).catch(err => storeHelper.handleError({ error: err, rethrow: false, timeout: 3000 }));
        },
        async loadExpenseById (hsabExpenseId: number, force = false, args?: { username: string, partnerId: number }) {
            if (!force && (this.expensesByIdFetchStateDictionary[hsabExpenseId] === FetchState.Loading ||
                this.expensesByIdFetchStateDictionary[hsabExpenseId] === FetchState.Loaded)) {
                return Promise.resolve(this.expensesByIdDictionary[hsabExpenseId]);
            }

            Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Loading);

            return expenseService.getHsabExpenseById(hsabExpenseId, args)
                .then(response => {
                    Vue.set(this.expensesByIdDictionary, hsabExpenseId, response.data);
                    Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Loaded);
                    return response.data;
                })
                .catch(_ => {
                    Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Failed);
                    return Promise.resolve();
                });
        },
        async loadExpenseWithRelationshipsById (hsabExpenseId: number, force = false, args?: { username: string, partnerId: number }) {
            if (!force && (this.expensesByIdFetchStateDictionary[hsabExpenseId] === FetchState.Loading ||
                this.expensesByIdFetchStateDictionary[hsabExpenseId] === FetchState.Loaded)) {
                return Promise.resolve(this.expensesByIdDictionary[hsabExpenseId]);
            }

            Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Loading);

            return expenseService.getHsabExpenseWithRelationshipsById(hsabExpenseId, args)
                .then(response => {
                    Vue.set(this.expensesByIdDictionary, hsabExpenseId, response.data);
                    Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Loaded);
                    return response.data;
                })
                .catch(_ => {
                    Vue.set(this.expensesByIdFetchStateDictionary, hsabExpenseId, FetchState.Failed);
                    return Promise.resolve();
                });
        },
        clearCachedExpenseById (expenseId: number) {
            this.expensesByIdFetchStateDictionary[expenseId] = FetchState.Uninitialized;
            Vue.delete(this.expensesByIdDictionary, expenseId);
        },
        async loadDebitCardReimbursementById (reimbursementId: number, args?: { username: string, partnerId: number }) {
            return expenseService.getUnusedDistributionById(reimbursementId, args).then(response => {
                return response.data;
            }).catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        async loadExpenseByExternalId (request: HsabExpenseByExternalIdRequest) {
            let externalId = null;
            if (request.externalReimbursementId) {
                externalId = request.externalReimbursementId;
            } else if (request.externalTransactionId) {
                externalId = request.externalTransactionId;
            } else {
                return Promise.resolve();
            }

            if (this.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loading ||
                this.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loaded) {
                return Promise.resolve(this.expensesByExternalIdDictionary[externalId]);
            }

            Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Loading);

            return expenseService.getHsabExpenseByExternalId(request)
                .then(response => {
                    Vue.set(this.expensesByExternalIdDictionary, externalId, response.data);
                    Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Loaded);
                    return response.data;
                })
                .catch(_ => {
                    Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Failed);
                    return Promise.resolve();
                });
        },
        async repayAllRepayments (reimbursementId: number) {
            return expenseService.repayHsabExpenseReimbursement(reimbursementId)
                .then(_ => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Expense repayments have been successfully scheduled.", 5000);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        DeactivateSchedule (hsabExpenseScheduleId: number) {
            return expenseService.deactivateHsabExpenseSchedule(hsabExpenseScheduleId)
                .then(async response => {
                    const hsabExpenseFormStore = useHsabExpenseFormStore();
                    const notifierStore = useNotifierStore();
                    const managedExpenseStore = useManagedExpenseStore();

                    managedExpenseStore.reloadCurrentPage();
                    this.clearCachedExpenseById(hsabExpenseFormStore.expense.hsabExpenseId);
                    hsabExpenseFormStore.updateModalMode(HsabExpenseFormMode.none);

                    notifierStore.setSuccess("Scheduled expenses have been cancelled", 5000);
                    return response.data;
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));

        },
    }
});
